import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
  HOME_ROUTE,
  LOGIN_ROUTE,
  setCurrentStep,
  setOtpSent,
  checkPhoneNumberExists,
  signUp,
} from "@hypexone/uicore";
import StepOne from "./StepOne";
import Button from "../../components/shared/Button/Button";
import SocialAuth from "../social-auth/SocialAuth";
import HeaderButtonComponent from "../../components/header/HeaderButtonComponent";
import RightArrowIcon from "../../../assets/icons/header-icons/right-arrow-white.svg";
import { getEnvVariable } from "../../../constants/brandConfig";
import { domain } from "../../../constants/Branding";
import androidIcon from "../../../assets/icons/android-icon.svg";
import downloadIcon from "../../../assets/icons/download-icon.svg";


const SignUpForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { otpSent, phoneNumberExists } = useAppSelector((state) => state.auth);
  const { langData } = useAppSelector((state) => state.cms);
  const { domainConfig } = useAppSelector((state) => state.common);
  const apkUrl = getEnvVariable(domain, "apk_url") || domainConfig?.apkUrl;

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [otp, setOtp] = useState<number>();
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const searchParams = new URLSearchParams(location.search);
  const campaignId = searchParams.get("campaignId");
  const refCodeFromUrl = searchParams.get("refCode");
  const [campaignCode, setCampaignCode] = useState<string>(campaignId ?? "");
  const referralCode = refCodeFromUrl ?? "";
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
  };
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  const [isUsernameValid, setIsUsernameValid] = useState(false);

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreedToTerms(e.target.checked);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const showPasswordClickHandler = () => {
    setShowPassword((prev) => !prev);
  };

  const handleDownloadApk = () => {
    if (!apkUrl) return;
    const href = apkUrl.startsWith("http") ? apkUrl : `https://${apkUrl}`;
    const link = document.createElement("a");
    link.href = href;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!otpSent || !otp || !username || !password) return;

    const rawCamp = campaignCode ?? "";
    const cleanCamp = rawCamp.trim().toLowerCase();
    const cleanRef = (referralCode ?? "").trim();
    setLoader(true);

    try {
      await dispatch(
        signUp({
          username: username.toLowerCase(),
          fullName: username.toLowerCase(),
          emailId: email,
          password,
          phoneNumber: "91" + phoneNumber,
          otp,
          referralCode: cleanRef,
          campaignCode: cleanCamp,
        })
      ).unwrap();
      navigate("/home");
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setLoader(false);
    }
  };

  const redirectToSignIn = () => {
    navigate(LOGIN_ROUTE);
  };

  useEffect(() => {
    if (campaignId) {
      localStorage.setItem("campaignId", campaignId);
    }
  }, [campaignId]);

  useEffect(() => {
    if (refCodeFromUrl) {
      localStorage.setItem("refCode", refCodeFromUrl);
    }
  }, [refCodeFromUrl]);

  useEffect(() => {
    dispatch(setCurrentStep(1));
    dispatch(setOtpSent(false));
  }, []);

  useEffect(() => {
    if (phoneNumber) {
      dispatch(checkPhoneNumberExists(phoneNumber));
    }
  }, [phoneNumber]);

  const isSubmitDisabled =
    !phoneNumber ||
    !agreedToTerms ||
    !isUsernameValid ||
    phoneNumberExists ||
    !otpSent ||
    !otp ||
    !username ||
    !password ||
    loader;

  return (
    <div className="relative z-10 flex h-full min-h-0 w-full flex-col overflow-hidden p-4 sm:px-4 sm:py-3">
      <div className="shrink-0">
        <HeaderButtonComponent
          leftIcon={{
            src: RightArrowIcon,
            alt: "right-arrow",
            customClassName: "h-2.5 w-2.5 scale-x-[-1]",
          }}
          onClick={() => navigate(HOME_ROUTE)}
          buttonText={langData?.["back"]}
        />
      </div>

      <div className="flex min-h-0 w-full flex-1 items-center justify-center px-1 sm:px-2">
        <div className="signup-form-card flex w-full max-w-[400px] flex-col overflow-hidden rounded-xl border border-white/15 bg-black/25 shadow-[0_12px_40px_rgba(0,0,0,0.25)] backdrop-blur-sm sm:max-w-md md:max-w-lg">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-3 sm:p-4 md:p-5">
            <img
              src={getEnvVariable(domain, "logo")}
              alt="logo"
              className="mx-auto mb-2 h-8 w-auto shrink-0 object-contain sm:mb-3 sm:h-9"
            />
            <header className="mb-2 shrink-0 text-center sm:mb-3">
              <h1 className="text-lg font-extrabold leading-tight text-white drop-shadow-md sm:text-xl md:text-2xl">
                {langData?.["sign_up"]}
              </h1>
              <p className="mt-1 text-[11px] leading-snug text-gray-200 sm:text-xs">
                {langData?.["create_your_account_txt"]}
              </p>
            </header>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-2 text-sm font-medium"
            >
              <StepOne
                otpSent={otpSent}
                otp={otp}
                setPhoneNumber={setPhoneNumber}
                phoneNumber={phoneNumber}
                agreedToTerms={agreedToTerms}
                email={email}
                onEmailChange={handleEmailChange}
                onPhoneChange={handlePhoneChange}
                onTermsChange={handleTermsChange}
                username={username}
                password={password}
                setOtp={setOtp}
                showPassword={showPassword}
                onUsernameChange={handleUsernameChange}
                onPasswordChange={handlePasswordChange}
                showPasswordClickHandler={showPasswordClickHandler}
                onUsernameValidityChange={setIsUsernameValid}
                campaignCode={campaignCode}
                onCampaignCodeChange={setCampaignCode}
                referralCode={referralCode}
              />

              <Button
                type="submit"
                disabled={isSubmitDisabled}
                loading={loader}
                className={`w-full shrink-0 rounded-xl py-2 text-xs font-semibold text-center text-white shadow-lg transition-all duration-200 min-h-[36px] sm:min-h-[44px] sm:py-3 sm:text-base ${isSubmitDisabled
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hover:opacity-90"
                  }`}
              >
                {langData?.["sign_up"]}
              </Button>

              {/* Social auth */}
              {domainConfig?.signup && domainConfig?.oauth_enabled && (
                  <div className="flex flex-col items-center text-gray-100 space-y-3">
                    <span className="text-sm">or continue with</span>
                    <SocialAuth />
                  </div>
                )}

              <div className="shrink-0 self-center pb-0.5 text-center text-[10px] leading-tight text-gray-200 sm:text-xs">
                {langData?.["already_have_an_account"]}{" "}
                <a
                  href="#"
                  onClick={redirectToSignIn}
                  className="text-yellow-300 underline font-semibold hover:text-yellow-400 transition-all duration-200"
                >
                  {langData?.["sign_in"]}
                </a>
              </div>
            </form>
          </div>
          {apkUrl && (
            <button
              type="button"
              className="w-full h-[40px] shrink-0 bg-[#66bb6a] text-white font-bold flex items-center justify-center gap-2.5 text-sm cursor-pointer hover:opacity-90 transition-opacity"
              onClick={handleDownloadApk}
            >
              <img src={androidIcon} alt="" className="h-5 w-5 flex-shrink-0" />
              <span>Download .apk</span>
              <img src={downloadIcon} alt="" className="h-5 w-[10px]" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUpForm;
